import { isEmptyInput, isNullOrUndefined } from '@adaptiva/core/common';
import { MenuController, MenuItem } from '@adaptiva/core/menus';
import { Injectable } from '@angular/core';
import { Observable, of } from 'rxjs';
import { MetadataAuthoringRule } from '../../../../common';
import { IAuthoringRuleNode } from '../interfaces';

@Injectable()
export class MainAuthoringRuleMenuController extends MenuController<IAuthoringRuleNode> {

	public readonly addOperatorMenuName: string = 'Add Operator';

	public readonly andMenuName: string = 'AND';

	public readonly notMenuName: string = 'NOT';

	public readonly orMenuName: string = 'OR';

	public readonly createExpressionMenuName: string = 'Create Sensor Expression';

	public readonly selectReferenceMenuName: string = 'Select Reference';

	public readonly removeMenuName: string = 'Remove';

	public readonly editExpressionMenuName: string = 'Edit Expression';

	public readonly editReferenceMenuName: string = 'Edit Reference';

	public override generate(_node: IAuthoringRuleNode): Observable<MenuItem<IAuthoringRuleNode>[]> {
		let _items: MenuItem<IAuthoringRuleNode>[];

		switch (_node?.type) {
			case MetadataAuthoringRule.EXPRESSION:
				_items = this.expressionMenuItems(_node);
				break;
			case MetadataAuthoringRule.REFERENCE:
				_items = this.referenceMenuItems(_node);
				break;
			default:
				_items = this.mainMenuItems(_node);
		}

		return of(_items);
	}

	public mainMenuItems(_node: IAuthoringRuleNode): MenuItem<IAuthoringRuleNode>[] {
		return [
			this.getAddOperatorMenuItem(_node),
			this.getCreateExpressionMenuItem(_node),
			this.getSelectReferenceMenuItem(_node),
			this.getRemoveMenuItem(_node),
		];
	}

	public expressionMenuItems(_node: IAuthoringRuleNode): MenuItem<IAuthoringRuleNode>[] {
		return [
			this.getEditExpressionMenuItem(_node),
			this.getRemoveMenuItem(_node),
		];
	}

	public referenceMenuItems(_node: IAuthoringRuleNode): MenuItem<IAuthoringRuleNode>[] {
		return [
			this.getEditReferenceMenuItem(_node),
			this.getRemoveMenuItem(_node),
		];
	}

	// #region Add

	protected getAddOperatorMenuItem(_node: IAuthoringRuleNode): MenuItem<IAuthoringRuleNode> {
		const _id: string = this.hashItem(this.addOperatorMenuName, _node);
		const _item: MenuItem<IAuthoringRuleNode> = this.createItem({
			id: _id,
			name: this.addOperatorMenuName,
			children: [
				this.getAndMenuItem(_node),
				this.getNotMenuItem(_node),
				this.getOrMenuItem(_node),
			]
		});

		this.enableAddOperatorMenu(_item);

		return _item;
	}

	protected enableAddOperatorMenu(_item: MenuItem<IAuthoringRuleNode>): void {
		_item.isDisabled.next(this._notOperatorIsPopulated(_item.data));
	}

	// #region And

	protected getAndMenuItem(_node: IAuthoringRuleNode): MenuItem<IAuthoringRuleNode> {
		const _id: string = this.hashItem(this.andMenuName, _node);
		const _item: MenuItem<IAuthoringRuleNode> = this.getItem(_id, _node) || this.createItem({
			id: _id,
			name: this.andMenuName,
			data: _node
		});

		return _item;
	}

	// #region Not

	protected getNotMenuItem(_node: IAuthoringRuleNode): MenuItem<IAuthoringRuleNode> {
		const _id: string = this.hashItem(this.notMenuName, _node);
		const _item: MenuItem<IAuthoringRuleNode> = this.getItem(_id, _node) || this.createItem({
			id: _id,
			name: this.notMenuName,
			data: _node
		});

		return _item;
	}

	// #region Or

	protected getOrMenuItem(_node: IAuthoringRuleNode): MenuItem<IAuthoringRuleNode> {
		const _id: string = this.hashItem(this.orMenuName, _node);
		const _item: MenuItem<IAuthoringRuleNode> = this.getItem(_id, _node) || this.createItem({
			id: _id,
			name: this.orMenuName,
			data: _node
		});

		return _item;
	}

	// #region Create Expression

	protected getCreateExpressionMenuItem(_node: IAuthoringRuleNode): MenuItem<IAuthoringRuleNode> {
		const _id: string = this.hashItem(this.createExpressionMenuName, _node);
		const _item: MenuItem<IAuthoringRuleNode> = this.getItem(_id, _node) || this.createItem({
			id: _id,
			name: this.createExpressionMenuName,
			data: _node
		});

		this.enableCreateExpressionMenu(_item);

		return _item;
	}

	protected enableCreateExpressionMenu(_item: MenuItem<IAuthoringRuleNode>): void {
		_item.isDisabled.next(this._notOperatorIsPopulated(_item.data));
	}

	// #region Selected Reference

	protected getSelectReferenceMenuItem(_node: IAuthoringRuleNode): MenuItem<IAuthoringRuleNode> {
		const _id: string = this.hashItem(this.selectReferenceMenuName, _node);
		const _item: MenuItem<IAuthoringRuleNode> = this.getItem(_id, _node) || this.createItem({
			id: _id,
			name: this.selectReferenceMenuName,
			data: _node,
			onSelect: (_menu: MenuItem<IAuthoringRuleNode>) => { }
		});

		this.enableSelectReferenceMenu(_item);

		return _item;
	}

	protected enableSelectReferenceMenu(_item: MenuItem<IAuthoringRuleNode>): void {
		_item.isDisabled.next(this._notOperatorIsPopulated(_item.data));
	}

	// #region Remove Menu

	protected getRemoveMenuItem(_node: IAuthoringRuleNode): MenuItem<IAuthoringRuleNode> {
		const _id: string = this.hashItem(this.removeMenuName, _node);
		const _item: MenuItem<IAuthoringRuleNode> = this.getItem(_id, _node) || this.createItem({
			id: _id,
			name: this.removeMenuName,
			data: _node
		});

		this.enableRemoveMenu(_item);

		return _item;
	}

	protected enableRemoveMenu(_item: MenuItem<IAuthoringRuleNode>): void {
		if (isNullOrUndefined(_item.data))
			_item.isVisible.next(false);

		_item.isDisabled.next(false);
	}

	// #region Edit Expression

	protected getEditExpressionMenuItem(_node: IAuthoringRuleNode): MenuItem<IAuthoringRuleNode> {
		const _id: string = this.hashItem(this.editExpressionMenuName, _node);
		const _item: MenuItem<IAuthoringRuleNode> = this.getItem(_id, _node) || this.createItem({
			id: _id,
			name: this.editExpressionMenuName,
			data: _node
		});

		this.enableEditExpressionMenu(_item);

		return _item;
	}

	protected enableEditExpressionMenu(_item: MenuItem<IAuthoringRuleNode>): void {
		_item.isDisabled.next(false);
	}

	// #region Edit Reference

	protected getEditReferenceMenuItem(_node: IAuthoringRuleNode): MenuItem<IAuthoringRuleNode> {
		const _id: string = this.hashItem(this.editReferenceMenuName, _node);
		const _item: MenuItem<IAuthoringRuleNode> = this.getItem(_id, _node) || this.createItem({
			id: _id,
			name: this.editReferenceMenuName,
			data: _node,
			onSelect: (_menu: MenuItem<IAuthoringRuleNode>) => { }
		});

		this.enableEditReferenceMenu(_item);

		return _item;
	}

	protected enableEditReferenceMenu(_item: MenuItem<IAuthoringRuleNode>): void {
		_item.isDisabled.next(false);
	}

	// #region Helpers

	protected override hashItem(_key: string, _node: IAuthoringRuleNode): string {
		return `${_key}::${_node?.id}`;
	}

	private _notOperatorIsPopulated(_node: IAuthoringRuleNode): boolean {
		return _node?.type === MetadataAuthoringRule.NOT && !isEmptyInput(_node?.children);
	}

}
